/**
 * Toast + Letter — the warm, non-intrusive notifications. Toast is a small transient
 * banner ("Pidge left you a berry"); Letter is a soft card for overnight community
 * events shown on welcome-back (CLAUDE.md: "letters, not a dashboard" — never a
 * stats panel, never guilt). Both auto-place and auto-dismiss; tapping dismisses
 * early. No timers leak — everything cleans up on scene shutdown.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING, TIMING } from './theme.js';
import { Panel } from './Panel.js';
import { makeClickable } from './clickable.js';

export interface ToastOptions {
  readonly text: string;
  readonly width?: number;
  /** ms on screen before auto-dismiss (0 = stay until tapped). */
  readonly duration?: number;
  readonly reducedMotion?: boolean;
  /** Accent stripe color (e.g. warm for a gift, accent for an event). */
  readonly accent?: number;
}

/** A small transient banner. Constructs, animates in, and self-destroys. */
export class Toast extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x: number, y: number, options: ToastOptions) {
    super(scene, x, y);
    const reduced = options.reducedMotion ?? false;
    const accent = options.accent ?? COLORS.accent;

    const padded = scene.add.text(0, 0, options.text, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.text,
      wordWrap: { width: (options.width ?? 320) - SPACING.lg },
    });
    padded.setOrigin(0.5);
    const w = options.width ?? Math.min(360, padded.width + SPACING.xl);
    const h = padded.height + SPACING.lg;

    const panel = new Panel(scene, 0, 0, { width: w, height: h, shadow: true });
    const stripe = scene.add.graphics();
    stripe.fillStyle(accent, 1);
    stripe.fillRoundedRect(-w / 2, -h / 2, 4, h, RADIUS.sm);

    this.add([panel, stripe, padded]);
    this.setSize(w, h);
    // Centered hit rect matching the drawn banner + a pointer cursor (tap-to-dismiss).
    makeClickable(this, w, h, { onClick: () => this.dismiss(reduced) });

    scene.add.existing(this);

    if (!reduced) {
      this.setAlpha(0);
      scene.tweens.add({ targets: this, alpha: 1, duration: TIMING.tap, ease: 'Sine.easeOut' });
    }

    const duration = options.duration ?? TIMING.toast;
    if (duration > 0) {
      const timer = scene.time.delayedCall(duration, () => this.dismiss(reduced));
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => timer.remove(false));
    }
  }

  dismiss(reduced = false): void {
    if (!this.scene) return;
    if (reduced) {
      this.destroy();
      return;
    }
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: TIMING.tap,
      ease: 'Sine.easeIn',
      onComplete: () => this.destroy(),
    });
  }
}

export interface LetterOptions {
  readonly title: string;
  readonly body: string;
  readonly width: number;
  /** Optional warm signature line ("— Maple"). */
  readonly from?: string;
}

/**
 * A soft "letter" card for an overnight community event. Sizes to its content; the
 * caller stacks these in a ScrollList on the welcome-back screen.
 */
export class Letter extends Phaser.GameObjects.Container {
  /** Full rendered height (so a ScrollList can track it). */
  readonly cardHeight: number;

  constructor(scene: Phaser.Scene, x: number, y: number, options: LetterOptions) {
    super(scene, x, y);
    const w = options.width;
    const innerW = w - SPACING.lg * 2;

    const title = scene.add.text(-w / 2 + SPACING.lg, 0, options.title, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.md}px`,
      color: CSS.warm,
      fontStyle: 'bold',
      wordWrap: { width: innerW },
    });
    const body = scene.add.text(-w / 2 + SPACING.lg, 0, options.body, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.text,
      wordWrap: { width: innerW },
      lineSpacing: 3,
    });
    const fromText = options.from
      ? scene.add.text(-w / 2 + SPACING.lg, 0, `— ${options.from}`, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.textMuted,
          fontStyle: 'italic',
        })
      : null;

    // Vertical stack with padding.
    const topPad = SPACING.lg;
    title.y = 0;
    body.y = title.height + SPACING.sm;
    if (fromText) fromText.y = body.y + body.height + SPACING.sm;
    const contentH =
      title.height + SPACING.sm + body.height + (fromText ? SPACING.sm + fromText.height : 0);
    const h = contentH + topPad * 2;
    this.cardHeight = h;

    // Now offset everything so the panel is centered on (x, y).
    const cursorY = -h / 2 + topPad;
    title.y = cursorY;
    body.y = cursorY + title.height + SPACING.sm;
    if (fromText) fromText.y = body.y + body.height + SPACING.sm;

    const panel = new Panel(scene, 0, 0, { width: w, height: h, fill: COLORS.surfaceAlt });
    const children: Phaser.GameObjects.GameObject[] = [panel, title, body];
    if (fromText) children.push(fromText);
    this.add(children);

    scene.add.existing(this);
  }
}
